import React from 'react';
import { Shield, FileText, Users, Clock, MousePointer2, ArrowLeft, Mail, CheckCircle2, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OrizonDeskPortal = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background pt-24 pb-20 px-4">
      <div className="max-w-4xl mx-auto space-y-12">
        
        {/* Dugme nazad */}
        <div className="relative z-50"> 
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8 group cursor-pointer bg-transparent border-none p-0"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {t('orizon_desk.back')}
          </button>
        </div>

        {/* Hero Section */}
        <div className="text-center space-y-6">
          {/* NOVA IKONA: Štit sa slovom O */}
          <div className="inline-flex items-center justify-center mb-4 transition-transform duration-300 hover:rotate-6 rotate-0">
            <div className="relative flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-[2rem] border border-primary/20 shadow-xl">
              <Shield size={50} strokeWidth={1.7} />
              <span className="absolute inset-0 flex items-center justify-center font-black text-2xl select-none" style={{ transform: 'translateY(-5%)' }}>
                O
              </span>
            </div>
          </div>

          <h1 className="text-5xl font-black uppercase tracking-tighter leading-none">
            {t('orizon_desk.hero_title_1')} <span className="text-primary text-6xl block">{t('orizon_desk.hero_title_2')}</span> {t('orizon_desk.hero_title_3')}
          </h1>
          <p className="text-muted-foreground text-[10px] uppercase tracking-[0.4em] font-black opacity-70">
            {t('orizon_desk.hero_subtitle')}
          </p>
        </div>

        {/* Glavni opis */}
        <section className="bg-card p-10 rounded-[3rem] border border-border shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-10 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
            <Zap size={200} />
          </div>
          <div className="relative z-10 space-y-6">
            <h2 className="text-2xl font-black uppercase tracking-tight text-foreground">
              {t('orizon_desk.main_desc_title')}
            </h2>
            <p className="text-muted-foreground leading-relaxed font-medium">
              {t('orizon_desk.main_desc_text')}
            </p>
            <div className="grid md:grid-cols-2 gap-4 pt-4">
              <div className="p-4 bg-muted/30 rounded-2xl border border-border transition-all hover:bg-muted/50">
                <h3 className="text-xs font-black uppercase text-primary mb-2">{t('orizon_desk.for_companies')}</h3>
                <p className="text-[13px] font-bold opacity-80">{t('orizon_desk.for_companies_desc')}</p>
              </div>
              <div className="p-4 bg-muted/30 rounded-2xl border border-border transition-all hover:bg-muted/50">
                <h3 className="text-xs font-black uppercase text-primary mb-2">{t('orizon_desk.for_workers')}</h3>
                <p className="text-[13px] font-bold opacity-80">{t('orizon_desk.for_workers_desc')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Šta sve portal nudi */}
        <div className="grid md:grid-cols-2 gap-6">
          <section className="bg-card p-8 rounded-[2.5rem] border border-border hover:border-primary/40 transition-all shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-6">
              <FileText size={24} />
            </div>
            <h2 className="text-lg font-black uppercase mb-4 tracking-tight">{t('orizon_desk.feature_1_title')}</h2>
            <ul className="space-y-3 text-[13px] text-muted-foreground font-bold uppercase">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_1_li1')}</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_1_li2')}</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_1_li3')}</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_1_li4')}</li>
            </ul>
          </section>

          <section className="bg-card p-8 rounded-[2.5rem] border border-border hover:border-primary/40 transition-all shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-6">
              <Clock size={24} />
            </div>
            <h2 className="text-lg font-black uppercase mb-4 tracking-tight">{t('orizon_desk.feature_2_title')}</h2>
            <p className="text-[13px] text-muted-foreground mb-4">
              {t('orizon_desk.feature_2_desc')}
            </p>
            <ul className="space-y-3 text-[13px] text-muted-foreground font-bold uppercase">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_2_li1')}</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_2_li2')}</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-primary" /> {t('orizon_desk.feature_2_li3')}</li>
            </ul>
          </section>
        </div>

        {/* Kako postati korisnik */}
        <section className="bg-primary text-primary-foreground p-10 rounded-[3rem] shadow-2xl space-y-8 relative overflow-hidden">
          <div className="absolute -bottom-10 -right-10 opacity-10">
            <Users size={300} />
          </div>
          <div className="relative z-10 text-center space-y-4">
            <h2 className="text-3xl font-black uppercase tracking-tighter italic">{t('orizon_desk.cta_title')}</h2>
            <p className="text-sm font-bold opacity-80 uppercase tracking-widest">{t('orizon_desk.cta_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 relative z-10">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 transition-all duration-300 hover:scale-105 hover:bg-white/20 cursor-default group/card">
              <span className="text-3xl font-black opacity-30 group-hover/card:opacity-60 transition-opacity">01</span>
              <h3 className="font-black uppercase text-sm mt-2">{t('orizon_desk.step_1_title')}</h3>
              <p className="text-xs mt-2 opacity-80">{t('orizon_desk.step_1_desc')}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 transition-all duration-300 hover:scale-105 hover:bg-white/20 cursor-default group/card">
              <span className="text-3xl font-black opacity-30 group-hover/card:opacity-60 transition-opacity">02</span>
              <h3 className="font-black uppercase text-sm mt-2">{t('orizon_desk.step_2_title')}</h3>
              <p className="text-xs mt-2 opacity-80">{t('orizon_desk.step_2_desc')}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/20 transition-all duration-300 hover:scale-105 hover:bg-white/20 cursor-default group/card">
              <span className="text-3xl font-black opacity-30 group-hover/card:opacity-60 transition-opacity">03</span>
              <h3 className="font-black uppercase text-sm mt-2">{t('orizon_desk.step_3_title')}</h3>
              <p className="text-xs mt-2 opacity-80">{t('orizon_desk.step_3_desc')}</p>
            </div>
          </div>
        </section>

        {/* Footer info */}
        <div className="pt-10 border-t border-border text-center space-y-6">
          <div className="space-y-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-black">
              {t('orizon_desk.footer_contact_text')}
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-10">
              <a href="mailto:info@orizon-aix.com" className="flex items-center gap-2 text-xl font-black text-primary hover:scale-110 transition-transform">
                <Mail size={20} /> info@orizon-aix.com
              </a>
              <a href="https://orizon-aix.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xl font-black text-primary hover:scale-110 transition-transform">
                <MousePointer2 size={20} /> orizon-aix.com
              </a>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground font-bold uppercase tracking-tighter opacity-50">
            © 2026 Orizon AIX. {t('orizon_desk.footer_rights')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrizonDeskPortal;